import { User } from '@/types/user';

/**
 * Check if the current device is mobile
 */
export const isMobileBrowser = (): boolean => {
    if (typeof window === 'undefined') return false;

    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return (
        /android/i.test(userAgent) ||
        (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) ||
        /windows phone/i.test(userAgent)
    );
};

/**
 * Check if user image is default/placeholder
 */
export const isExistingUserImageDefault = (profileImage?: string): boolean => {
    if (!profileImage) return true;

    const defaultImages = ['no_user.png'];

    return defaultImages.some(defaultImg => profileImage.includes(defaultImg));
};

/**
 * Map React user object to Userflow user data format
 */
export interface UserflowUserData {
    name: string;
    username: string;
    email: string;
    userId: string | number;
    signed_up_at?: string;
    profile_image?: string | null;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    mobile_browser?: boolean;
    [key: string]: any; // Index signature for userflow compatibility
}

export const mapUserToUserflowData = (user: User): UserflowUserData => {
    const userData: UserflowUserData = {
        name: user.full_name,
        username: user.username,
        email: user.email,
        userId: user.user_id,
        signed_up_at: new Date(user.created_at * 1000).toISOString().split('T')[0],
        profile_image: isExistingUserImageDefault(user?.data?.profile_image) ? null : user?.data?.profile_image || null,
        mobile_browser: isMobileBrowser(),
    };

    // Add UTM parameters if they exist
    const acquisition = user.data?.acquisition;
    if (acquisition?.utm_source) {
        userData.utm_source = acquisition.utm_source;
    }
    if (acquisition?.utm_medium) {
        userData.utm_medium = acquisition.utm_medium;
    }
    if (acquisition?.utm_campaign) {
        userData.utm_campaign = acquisition.utm_campaign;
    }

    return userData;
};

/**
 * Build user attributes object for bulk updates
 * Includes experiments, metadata, and integration status
 */
export const buildUserflowAttributes = (user: User): Record<string, any> => {
    const attributes: Record<string, any> = {};

    // Add experiments
    if (user.experiments) {
        for (const experiment in user.experiments) {
            attributes[experiment] = user.experiments[experiment];
        }
    }

    // Add metadata
    if (user.metadata) {
        for (const key in user.metadata) {
            attributes[key] = user.metadata[key];
        }
    }

    // Add integration status
    if (user.integrations) {
        const stripeIntegration = Object.values(user.integrations).find(int => int.type === 'stripe-connect');
        const paypalIntegration = Object.values(user.integrations).find(int => int.type === 'paypal');

        if (stripeIntegration) {
            attributes.is_stripe_connected = stripeIntegration.status === 1;
        }

        if (paypalIntegration) {
            attributes.is_paypal_connected = paypalIntegration.status === 1;
        }
    }

    return attributes;
};
